import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { itemsV2ControllerGetSupplierPrice } from "@semo/api-contracts";

/**
 * Result type for a single supplier price lookup
 */
export interface SupplierPriceResult {
  itemId: string | null;
  supplierPrice: number | null;
  hasHistory: boolean;
  itemName: string | null;
}

/**
 * Hook: Batch fetch supplier prices for multiple items
 *
 * Use case: Auto-fill supplier prices on quote response embed page
 *
 * Algorithm:
 * 1. Filter items with valid itemId (UUID)
 * 2. Create parallel queries for each item using useItemsV2ControllerGetSupplierPrice
 * 3. Use useQueries for efficient batch loading
 * 4. Build Map<string, SupplierPriceResult> for O(1) lookup
 * 5. Return helper function for safe price retrieval
 *
 * @param items - Array of quote request items
 * @param supplierId - Supplier ID for price lookup
 * @returns Object with priceMap, isLoading, error, and getSupplierPrice helper
 */
export function useSupplierPrices(
  items: { itemId?: string }[],
  supplierId: string | undefined,
) {
  // Filter items with valid identifier
  const validItems = useMemo(
    () => items.filter((item) => !!item.itemId),
    [items],
  );

  // Create queries for batch fetching
  const queries = useMemo(
    () =>
      validItems.map((item) => ({
        queryKey: ["items-v2", "supplier-price", item.itemId, supplierId],
        queryFn: () => {
          return itemsV2ControllerGetSupplierPrice({
            itemId: item.itemId!,
            supplierId: supplierId || "",
          });
        },
        // Only run query if we have an ID and a supplierId
        enabled: !!item.itemId && !!supplierId,
      })),
    [validItems, supplierId],
  );

  // Execute batch queries and build price map for O(1) lookup
  const { priceMap, isLoading, error } = useQueries({
    queries,
    combine: (results) => {
      const map = new Map<string, SupplierPriceResult>();

      results.forEach((result, index) => {
        const item = validItems[index];
        if (!item?.itemId) return;

        if (result.data) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const responseData = (result.data as any).data as {
            supplierPrice?: number | null;
            hasHistory?: boolean;
            itemName?: string | null;
          };

          const priceResult: SupplierPriceResult = {
            itemId: item.itemId,
            supplierPrice: responseData?.supplierPrice ?? null,
            hasHistory: responseData?.hasHistory ?? false,
            itemName: responseData?.itemName ?? null,
          };

          map.set(item.itemId, priceResult);
        } else if (result.isError) {
          // No history for this item or error
          const emptyResult: SupplierPriceResult = {
            itemId: item.itemId,
            supplierPrice: null,
            hasHistory: false,
            itemName: null,
          };

          map.set(item.itemId, emptyResult);
        }
      });

      return {
        priceMap: map,
        isLoading: results.some((result) => result.isLoading),
        error: results.find((result) => result.error)?.error ?? null,
      };
    },
  });

  // Helper function for safe price retrieval
  const getSupplierPrice = (id: string | null | undefined): number | null => {
    if (id == null) return null;
    return priceMap.get(id)?.supplierPrice ?? null;
  };

  return {
    priceMap,
    isLoading,
    error,
    getSupplierPrice,
  };
}
